import moment from 'moment-timezone'
import { isNil } from 'ramda'

import {
  DEFAULT_TIME_ZONE_NAME,
  MINUTE_MS,
} from '~/src/components/shared/features/constants'

import { isoTimeFromObj, prettyTimeFromIso, prettyTimeFromObj } from '~/helpers/time'

type DateTimeFormatOptions = {
  weekday?: 'long' | 'short' | 'narrow'
  timeZoneName?:
    | 'long'
    | 'short'
    | 'shortOffset'
    | 'longOffset'
    | 'shortGeneric'
    | 'longGeneric'
}

const weekDaysDictionary: { [key: string]: string | undefined } = {
  Sunday: 'Su',
  Monday: 'M',
  Tuesday: 'T',
  Wednesday: 'W',
  Thursday: 'Th',
  Friday: 'F',
  Saturday: 'Sa',
}

const prettyDateFromIso = (
  isoString: string | null,
  excludeYear = false,
  shortenYear = false
): string => {
  if (!isoString) return ''

  const [year, month, day] = isoString.split(/[^0-9]/)
  const updatedYear = shortenYear ? year?.slice(-2) : year

  return excludeYear ? [month, day].join('/') : [month, day, updatedYear].join('/')
}

const prettyDateTimeFromIso = (
  isoString: string | null,
  excludeYear = false
): string => {
  if (!isoString) return ''

  return `${prettyTimeFromIso(isoString)} ${prettyDateFromIso(isoString, excludeYear)}`
}

const prettyDateFromObj = (date: Date, excludeYear = false): string => {
  const year = date.getFullYear()
  let month = '' + (date.getMonth() + 1)
  let day = '' + date.getDate()

  if (month.length < 2) month = '0' + month
  if (day.length < 2) day = '0' + day

  return excludeYear ? [month, day].join('/') : [month, day, year].join('/')
}

/**
 * Helper to convert date object to a format which includes weekday and time zone: [Week day] [Time] [Time Zone]
 *
 * @example
 * 2023-02-02 04:09:46.336667 => M 08:30 CT
 */
const prettyDateTimeFromObj = (
  date: Date,
  timeZone = true,
  options: DateTimeFormatOptions = { weekday: 'long', timeZoneName: 'short' }
): string => {
  const localeTimeStringDate = date.toLocaleTimeString('en-us', options).split(' ')

  const weekDay = localeTimeStringDate[0].replace(',', '')
  const tz = localeTimeStringDate[3]

  const result = `${
    options.weekday === 'long' ? weekDaysDictionary[weekDay] : weekDay
  } ${prettyTimeFromObj(date)}`

  return timeZone ? `${result} ${tz}` : result
}

/**
 * Helper to convert date to provided timezone and return the formatted date
 * @param date - date to convert
 * @param timeZoneName  - timezone to convert to
 * @returns
 */
const convertToTimezone = (date: string, timeZoneName: string): string => {
  return moment(date).tz(timeZoneName).format('MM/DD/YYYY HH:mm')
}

/**
 * Helper to convert date to UTC from local time
 * @param time  - time to convert
 * @param timeZoneName - timezone to convert from
 * @returns
 */
const convertBackToUtc = (time: string, timeZoneName: string | undefined) => {
  const editedLocalMoment = moment.tz(
    time,
    'MM/DD/YYYY HH:mm',
    timeZoneName ?? DEFAULT_TIME_ZONE_NAME
  )
  const backToUtc = editedLocalMoment.utc().format()

  return backToUtc
}

// TODO: standardize date and time formatting across the app
// https://app.asana.com/1/1187292652299643/project/1209572988416484/task/1210893576993950?focus=true
/**
 * Formats a JS Date and time string as MM/DD/YYYY HH:mm for conversion.
 */
const formatLocalDateTimeString = (date: Date, time: string): string => {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const year = date.getFullYear()

  return `${month}/${day}/${year} ${time}`
}

// Returns only the date portion in ISO format
const isoDateFromPretty = (pretty: string): string => {
  if (!pretty) return ''

  const [month, day, year] = pretty.split('/')

  return [year, month, day].join('-')
}

// Returns only the date portion in ISO format
const isoDateFromObj = (date: Date): string => {
  const year = date.getFullYear()
  let month = '' + (date.getMonth() + 1)
  let day = '' + date.getDate()

  if (month.length < 2) month = '0' + month
  if (day.length < 2) day = '0' + day

  return [year, month, day].join('-')
}

// Returns ISO format without specifying TZ
const isoDateTimeFromObj = (date: Date): string =>
  `${isoDateFromObj(date)}T${isoTimeFromObj(date)}`

const partsFromIso = (
  isoString: string | null
): {
  year: string | null
  month: string | null
  day: string | null
  hours: string | null
  minutes: string | null
  seconds: string | null
} => {
  if (!isoString) {
    return {
      year: null,
      month: null,
      day: null,
      hours: null,
      minutes: null,
      seconds: null,
    }
  }

  const [year, month, day, hours, minutes, seconds] = isoString.split(/[^0-9]/)

  return { year, month, day, hours, minutes, seconds }
}

const parseDate = (
  date: string
): { month: number | null; day: number | null; year: number | null } => {
  if (!date) return { month: null, day: null, year: null }

  let month, day, year

  if (date.includes('/')) {
    ;[month, day, year] = date.replace(/[^0-9/]/g, '').split('/')
  } else if (date.includes('-')) {
    ;[month, day, year] = date.replace(/[^0-9-]/g, '').split('-')
  } else {
    const onlyNumbers = date.replace(/[^0-9]/g, '')
    const numDigits = onlyNumbers.length

    if (numDigits < 5) {
      month = onlyNumbers.substring(numDigits - 4, numDigits - 2)
      day = onlyNumbers.substring(numDigits - 2, numDigits)
    } else if (numDigits < 7) {
      month = onlyNumbers.substring(numDigits - 6, numDigits - 4)
      day = onlyNumbers.substring(numDigits - 4, numDigits - 2)
      year = onlyNumbers.substring(numDigits - 2, numDigits)
    } else {
      month = onlyNumbers.substring(numDigits - 8, numDigits - 6)
      day = onlyNumbers.substring(numDigits - 6, numDigits - 4)
      year = onlyNumbers.substring(numDigits - 4, numDigits)
    }
  }

  month = parseInt(month)
  day = parseInt(day)
  year = year ? parseInt(year) : new Date().getFullYear()

  if (year < 1000) year += 2000

  if (!month || !day || month > 12 || day > 31) {
    // month or day is missing/invalid, parsing most likely failed, reject whole date
    month = null
    day = null
    year = null
  }

  return { month, day, year }
}

// https://stackoverflow.com/a/54127122
const convertTimezone = (date: Date | string, newTz: string): Date => {
  return date instanceof Date
    ? new Date(date.toLocaleString('en-US', { timeZone: newTz }))
    : new Date(new Date(date).toLocaleString('en-US', { timeZone: newTz }))
}

const isWeekday = (date: Date) => ![0, 6].includes(date.getDay())

const getDaysInMonth = (date: Date) =>
  32 - new Date(date.getFullYear(), date.getMonth(), 32).getDate()

const getNumWeekdaysInMonth = (date: Date, excludeFuture = false): number => {
  const nowMS = new Date().getTime()
  const numDays = getDaysInMonth(date)
  let weekdays = 0

  for (let i = 0; i < numDays; i++) {
    const dateToCheck = new Date(date.getFullYear(), date.getMonth(), i + 1)

    if (!(excludeFuture && dateToCheck.getTime() > nowMS) && isWeekday(dateToCheck)) {
      weekdays++
    }
  }

  return weekdays
}

const addDays = (date: Date, numDays: number): Date =>
  new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate() + numDays,
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
    date.getMilliseconds()
  )

const getStartOfWeek = (date = new Date()): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() - date.getDay())

const getDateRanges = ({
  numDates,
  daysInbetween,
  start = new Date(),
}: {
  numDates: number
  daysInbetween: number
  start?: Date
}): Array<Date> =>
  Array.from(Array(numDates).keys()).map(
    (i) =>
      new Date(
        start.getFullYear(),
        start.getMonth(),
        start.getDate() + i * daysInbetween
      )
  )

const isToday = (date: Date): boolean =>
  prettyDateFromObj(date) === prettyDateFromObj(new Date())

const diffInSeconds = (d1: Date, d2: Date): number =>
  (d1.getTime() - d2.getTime()) / 1000

/**
 * Compares two dates to determine if the first date is before the second date
 *
 * @param date - The date to compare, can be a Date object or date string
 * @param compareToDate - The date to compare against, defaults to current datetime. Can be a Date object or date string
 * @returns boolean indicating if date is before compareToDate
 *
 * @example
 * isDateBeforeComparison('2023-01-01', '2023-12-31') // returns true
 * isDateBeforeComparison('2023-01-01') // returns false because it is in the past
 */
const isDateBeforeComparison = (
  date: Date | string,
  compareToDate: Date | string = new Date()
): boolean => {
  if (typeof date === 'string') {
    date = new Date(date)
  }
  if (typeof compareToDate === 'string') {
    compareToDate = new Date(compareToDate)
  }

  return date.getTime() < compareToDate.getTime()
}

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

const DAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
]

/**
 * Helper to combine the date for one Date with the time of another Date
 *
 * @example
 * combineDateAndTime(new Date(2023, 03, 03), new Date(2022, 02, 02, 02, 02, 02)) => 2023-03-03 02:02:02
 */
const combineDateAndTime = (date: Date | undefined, time: Date | undefined): Date => {
  if (date && time) {
    const year = date.getFullYear()
    const month = date.getMonth()
    const day = date.getDate()
    const hours = time.getHours()
    const minutes = time.getMinutes()
    const seconds = time.getSeconds()
    const milliseconds = time.getMilliseconds()

    return new Date(year, month, day, hours, minutes, seconds, milliseconds)
  } else if (!date && time) {
    return time
  } else if (date && !time) {
    return date
  }

  return new Date()
}

const toLocalISODateString = (date: Date): string => {
  return moment(date).format(`YYYY-MM-DD`)
}

/*
 * Converts a date string in the format 'YYYY-MM-DD' to a Date object
 * that is at midnight in the local timezone
 */
const getLocalDateFromString = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number)

  return new Date(year, month - 1, day)
}

const getMinutesAgo = (timestamp: number): number => {
  if (timestamp > Date.now()) {
    return 0
  }

  return Math.floor((Date.now() - timestamp) / (1000 * 60))
}

/**
 * Milliseconds elapsed since a timestamp
 */
const getElapsedMs = (timestamp: string | Date | null | undefined): number | null => {
  if (!timestamp) {
    return null
  }

  const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp
  const timestampMs = date.getTime()

  if (Number.isNaN(timestampMs)) {
    return null
  }

  return Date.now() - timestampMs
}

const isLessThanMinute = (elapsedMs: number): boolean => {
  return elapsedMs < MINUTE_MS
}

/**
 * Formats elapsed milliseconds as a relative duration (e.g. "2h 30m")
 */
const formatRelativeDurationFromMs = (
  elapsedMs: number,
  t?: (key: string, options?: Record<string, string | number>) => string
): string => {
  if (isLessThanMinute(elapsedMs)) {
    return ''
  }

  const diffMinutes = Math.floor(elapsedMs / (1000 * 60))
  const diffHours = Math.floor(elapsedMs / (1000 * 60 * 60))
  const diffDays = Math.floor(elapsedMs / (1000 * 60 * 60 * 24))

  const days = diffDays
  const hours = diffHours % 24
  const minutes = diffMinutes % 60

  const timeParts: Array<string> = []
  if (t) {
    if (days > 0) timeParts.push(t('timeUnits.days', { count: days }))
    if (hours > 0) timeParts.push(t('timeUnits.hours', { count: hours }))
    if (minutes > 0) timeParts.push(t('timeUnits.minutes', { count: minutes }))
  } else {
    if (days > 0) timeParts.push(`${days}d`)
    if (hours > 0) timeParts.push(`${hours}h`)
    if (minutes > 0) timeParts.push(`${minutes}m`)
  }

  if (timeParts.length === 0) {
    return ''
  }

  return timeParts.join(' ')
}

/**
 * Formats a timestamp as a relative duration (e.g. "2h 30m")
 */
const formatRelativeDuration = (
  timestamp: string | Date | null | undefined,
  t?: (key: string, options?: Record<string, string | number>) => string
): string => {
  const elapsedMs = getElapsedMs(timestamp)

  if (isNil(elapsedMs)) {
    return ''
  }

  return formatRelativeDurationFromMs(elapsedMs, t)
}

/**
 * Converts a timestamp to a human-readable "time ago" string.
 * @param timestamp - ISO timestamp string or Date
 * @param t - Optional translation function for i18n support
 * @returns Formatted string like "less than a minute ago"
 */
const getTimeAgo = (
  timestamp: string | Date | null | undefined,
  t?: (key: string, options?: Record<string, string | number>) => string
): string => {
  const elapsedMs = getElapsedMs(timestamp)

  if (isNil(elapsedMs)) {
    return ''
  }

  if (isLessThanMinute(elapsedMs)) {
    return t ? t('timeUnits.now') : 'less than a minute ago'
  }

  const duration = formatRelativeDurationFromMs(elapsedMs, t)

  return t ? t('timeAgo', { time: duration }) : `${duration} ago`
}

/**
 * Formats a duration in seconds as "hh:mm"
 *
 * @param seconds - The number of seconds to format
 */
const formatDuration = (seconds: number): string => {
  if (seconds <= 0) return '00:00'

  // round up because < 60s should be displayed as 1m
  const minutesLeft = Math.ceil(seconds / 60)

  const displayHours = Math.floor(minutesLeft / 60)
  const displayMinutes = minutesLeft % 60

  return `${String(displayHours).padStart(2, '0')}:${String(displayMinutes).padStart(
    2,
    '0'
  )}`
}

const getTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

export {
  addDays,
  combineDateAndTime,
  convertBackToUtc,
  convertTimezone,
  convertToTimezone,
  DAY_NAMES,
  diffInSeconds,
  formatLocalDateTimeString,
  getDateRanges,
  getNumWeekdaysInMonth,
  getStartOfWeek,
  isDateBeforeComparison,
  isoDateFromObj,
  isoDateFromPretty,
  isoDateTimeFromObj,
  isToday,
  MONTH_NAMES,
  parseDate,
  partsFromIso,
  prettyDateFromIso,
  prettyDateFromObj,
  prettyDateTimeFromIso,
  prettyDateTimeFromObj,
  toLocalISODateString,
  getLocalDateFromString,
  getMinutesAgo,
  getElapsedMs,
  isLessThanMinute,
  formatRelativeDuration,
  formatRelativeDurationFromMs,
  getTimeAgo,
  formatDuration,
  getTimezone,
}
